import React, { useMemo, useCallback, memo } from 'react'
import styled from 'styled-components'
import { formatNumberWithTruncation, plusComma } from '../../js/script'
import { handlePostMessage } from '../../js/postMessage'
import { useAppContext } from '../provider/AppProvider'
import { useCommonContext } from '../provider/CommonProvider'
import { ProductDetailType, ProductDetailBaseInfo } from '../../@types/product'

interface ProductDiscountInfoProps {
  product: ProductDetailType
  productItem?: ProductDetailBaseInfo
  disCountAcc: number
}

// 이미지 경로
const imgURL = '/images'

const ProductDiscountInfo: React.FC<ProductDiscountInfoProps> = ({ product, productItem, disCountAcc }) => {
  const { setIsLogin, hasAccessToken } = useCommonContext()
  const { isAppView } = useAppContext()

  const joinUsClickHandler = useCallback(() => {
    if (isAppView) {
      handlePostMessage({
        actionType: 'onSignInInducement',
      })
    } else {
      setIsLogin(true)
    }
  }, [isAppView, setIsLogin])

  const getExpectedPrice = useCallback(
    (product: ProductDetailType, productItem: ProductDetailBaseInfo, disCountAcc: any) => {
      if (!product.price?.salePrice) return 0
      const basePrice = product.price.salePrice
      const immediateDiscount = product.price.immediateDiscountAmt
      const couponDiscount = product.price.couponDiscountAmt || 0
      const accumulationAmt = product.price.accumulationAmtWhenBuyConfirm || 0
      const discountAcc = productItem?.accumulationUseYn === 'N' ? 0 : Number(disCountAcc)

      return plusComma(
        Math.ceil(basePrice - (immediateDiscount + couponDiscount + accumulationAmt) - discountAcc) < 0
          ? 0
          : basePrice - (immediateDiscount + couponDiscount + accumulationAmt) - discountAcc
      )
    },
    []
  )

  const renderExpectedPrice = useCallback(() => {
    if (!productItem) return null

    const getNonMemberPrice = () => {
      const nonMemberPrice = product.price?.salePrice
        ? plusComma(product.price.salePrice - product.price.immediateDiscountAmt)
        : 0

      return (
        <>
          <dt>비회원 적용가</dt>
          {product.limitations?.memberOnly ? <dd>비회원 구매불가</dd> : <dd>{nonMemberPrice}원</dd>}
        </>
      )
    }

    const getMemberPrice = () => (
      <>
        <dt>최대 할인 예상 금액</dt>
        <dd>{getExpectedPrice(product, productItem, disCountAcc)}원</dd>
      </>
    )

    return !hasAccessToken ? getNonMemberPrice() : getMemberPrice()
  }, [product, productItem, disCountAcc, hasAccessToken, getExpectedPrice])

  const isRenderDiscountDetails = useMemo(() => {
    return (
      product.price?.maxCouponAmt !== 0 ||
      product.price?.accumulationAmtWhenBuyConfirm !== 0 ||
      (productItem?.accumulationUseYn !== 'N' && Number(disCountAcc) !== 0)
    )
  }, [product, productItem, disCountAcc])

  const renderDiscountItem = useCallback((condition: boolean, title: string, value: string | number) => {
    if (!condition) return null
    return (
      <dl>
        <dt>{title}</dt>
        <dd>{value}원</dd>
      </dl>
    )
  }, [])

  const renderDiscountDetails = useCallback(() => {
    if (!isRenderDiscountDetails) return null

    return (
      <>
        <hr />
        <div className='discount'>
          {renderDiscountItem(
            productItem?.couponUseYn !== 'N' && product.price?.couponDiscountAmt !== 0,
            '쿠폰 최대 할인',
            product.price ? `-${plusComma(product.price?.couponDiscountAmt)}` : 0
          )}

          {renderDiscountItem(
            productItem?.accumulationUseYn !== 'N' && Number(disCountAcc) !== 0,
            '적립금 최대 할인',
            product.price?.salePrice
              ? `- ${formatNumberWithTruncation(
                  (product.price?.salePrice - product.price?.immediateDiscountAmt) * 0.1
                )}`
              : 0
          )}

          {product.price?.accumulationAmtWhenBuyConfirm !== 0 && (
            <dl>
              <dt>{`페이백 적립금(기본 ${product.price?.accumulationRate ? product.price?.accumulationRate : 0}%)`}</dt>
              <dd>
                {product.price?.accumulationAmtWhenBuyConfirm
                  ? `-${plusComma(product.price?.accumulationAmtWhenBuyConfirm)}`
                  : 0}
                <img src={`${imgURL}/icon_point.png`} alt='' />
              </dd>
            </dl>
          )}
        </div>
      </>
    )
  }, [isRenderDiscountDetails, product, productItem, disCountAcc, renderDiscountItem])

  const renderMembershipPrice = useCallback(() => {
    const isNoDiscountAvailable =
      product.price?.maxCouponAmt === 0 &&
      product.price?.accumulationAmtWhenBuyConfirm === 0 &&
      (productItem?.accumulationUseYn === 'N' || Number(disCountAcc) === 0)
    if (isNoDiscountAvailable) return null

    const isCouponDiscountAvailable = productItem?.couponUseYn !== 'N' && product.price?.maxCouponAmt !== 0
    const isAccumulationDiscountAvailable = productItem?.accumulationUseYn !== 'N' && Number(disCountAcc) !== 0
    const isPaybackAvailable = product.price?.accumulationAmtWhenBuyConfirm !== 0

    const calculateMemberPrice = () => {
      if (!product.price?.salePrice) return 0

      const immediateDiscount = product.price?.immediateDiscountAmt || 0
      const couponDiscountAmt = product.price?.couponDiscountAmt || 0
      const accumulationAmtWhenBuyConfirm =
        product.price?.accumulationAmtWhenBuyConfirm !== 0 ? product.price?.accumulationAmtWhenBuyConfirm : 0
      const discountAcc = productItem?.accumulationUseYn === 'N' ? 0 : Number(disCountAcc)

      const memberPrice =
        product.price?.salePrice -
          (immediateDiscount + couponDiscountAmt + accumulationAmtWhenBuyConfirm) -
          discountAcc <
        0
          ? 0
          : product.price?.salePrice -
            (immediateDiscount + couponDiscountAmt + accumulationAmtWhenBuyConfirm) -
            discountAcc

      return plusComma(Math.ceil(memberPrice))
    }

    return (
      <div className='membership_price'>
        <hr />
        <dl className='expected'>
          <dt>회원 적용가</dt>
          <dd>{calculateMemberPrice()}원</dd>
        </dl>
        {isCouponDiscountAvailable && (
          <dl>
            <dt>쿠폰 최대 할인</dt>
            <dd>{product.price?.couponDiscountAmt ? `-${plusComma(product.price?.couponDiscountAmt)}` : 0}원</dd>
          </dl>
        )}

        {isAccumulationDiscountAvailable && (
          <dl>
            <dt>적립금 최대 할인</dt>
            <dd>
              {product.price?.salePrice
                ? `- ${formatNumberWithTruncation(
                    (product.price?.salePrice - product.price?.immediateDiscountAmt) * 0.1
                  )}`
                : 0}
              원
            </dd>
          </dl>
        )}

        {isPaybackAvailable && (
          <dl>
            <dt>{`페이백 적립금(기본 ${product.price?.accumulationRate ? product.price?.accumulationRate : 0}%)`}</dt>
            <dd>
              {product.price?.accumulationAmtWhenBuyConfirm
                ? `-${plusComma(product.price?.accumulationAmtWhenBuyConfirm)}`
                : 0}
              <img src={`${imgURL}/icon_point.png`} alt='' />
            </dd>
          </dl>
        )}
        <button className='btn_join' onClick={joinUsClickHandler}>
          회원가입하기
        </button>
      </div>
    )
  }, [product, productItem, disCountAcc, isAppView, setIsLogin])

  return (
    <ProductDiscountSection>
      <dl className='expected'>{renderExpectedPrice()}</dl>

      {/* 상품 가격 */}
      <div className='price'>
        <dl>
          <dt>정상가</dt>
          <dd>{product.price?.salePrice ? plusComma(product.price?.salePrice) : 0}원</dd>
        </dl>
        <dl>
          <dt>상품 할인</dt>
          <dd>{product.price?.immediateDiscountAmt ? `- ${plusComma(product.price?.immediateDiscountAmt)}` : 0}원</dd>
        </dl>
      </div>

      {/* 구분선 */}
      {hasAccessToken ? renderDiscountDetails() : renderMembershipPrice()}
    </ProductDiscountSection>
  )
}

export default memo(ProductDiscountInfo)

export const ProductDiscountSection = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  border-radius: 4px;
  border: 1px solid #eaeaea;
  background: #fff;
  padding: 16px 20px 16px 16px;
  margin-top: 16px;
  & .price {
    & dl dd.origin_sale {
      text-decoration: line-through;
    }
  }
  & dl {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  & dl dd {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1px;
    & img {
      width: 12px;
      height: 12px;
    }
  }
  & .membership_price {
    color: var(--colGray6);
    & dl:last-of-type {
      margin-bottom: 16px;
    }
    & button.btn_join {
      display: flex;
      width: 100%;
      height: 36px;
      padding: 0px 10px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      color: var(--colWhite);
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      background: var(--colBlack);
      color: var(--colWhite);
      border-radius: 5px;
      border: none;
    }
  }

  & dl.expected {
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
    margin-bottom: 14px;
    @media screen and (min-width: 768px) {
      font-size: 16px;
    }
  }
  & div {
    & dl:last-child {
      margin-bottom: 0;
    }
  }
  & hr {
    display: block;
    height: 1px;
    border: none;
    background: #eaeaea;
    margin: 16px 0;
  }
`
